// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/system-architecture.md

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Avatar,
  Stack,
  CircularProgress,
  Fade,
  IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatNavigationIcon from '@mui/icons-material/Chat';
import ReactMarkdown from 'react-markdown';
import { apiClient, API_ENDPOINTS, CHAT_REQUEST_TIMEOUT_MS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import ChatBotIcon from './ChatBotIcon';
import PageSectionHeader from './common/PageSectionHeader';

/** Unique React keys for chat rows (avoids duplicate Date.now() collisions). */
function chatMessageId(prefix = 'm') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Double-quote knowledge titles in the subtitle (escape embedded "). */
function formatQuotedKnowledgeTitles(titles) {
  return titles
    .filter(Boolean)
    .map((t) => {
      const s = String(t).trim();
      if (!s) return null;
      const escaped = s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `"${escaped}"`;
    })
    .filter(Boolean)
    .join(', ');
}

/** PageSectionHeader copy from GET /chat/rag-context (linked KB + performance hint). */
function buildChatHeaderDescription(ragCtx) {
  const base = 'Start or continue a conversation with Nexus-AI.';
  if (!ragCtx || !ragCtx.rag_enabled) {
    return base;
  }
  if (ragCtx.scope === 'none') {
    return `${base} Knowledge retrieval is not used for this chat.`;
  }
  if (ragCtx.scope === 'full') {
    return `${base} Your team knowledge base is in use for this chat. Responses may be slower than usual.`;
  }
  const titles = (ragCtx.linked_titles || []).filter(Boolean);
  const quoted = formatQuotedKnowledgeTitles(titles);
  if (quoted) {
    return `${base} This chat is linked to the following knowledge: ${quoted}. Responses may be slower than usual.`;
  }
  return `${base} This chat uses scoped knowledge. Responses may be slower than usual.`;
}

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [conversationContext, setConversationContext] = useState(null);
  const [ragContext, setRagContext] = useState(null);
  const messagesEndRef = useRef(null);
  
  const { getToken, user } = useAuth();

  // Session storage keys - configurable via environment
  const STORAGE_KEYS = {
    MESSAGES: import.meta.env.VITE_CHAT_STORAGE_MESSAGES || 'nexus_chat_messages',
    SESSION_ID: import.meta.env.VITE_CHAT_STORAGE_SESSION_ID || 'nexus_chat_session_id',
    CONTEXT: import.meta.env.VITE_CHAT_STORAGE_CONTEXT || 'nexus_chat_context',
    USER_ID: import.meta.env.VITE_CHAT_STORAGE_USER_ID || 'nexus_chat_user_id'
  };

  // Debug logging
  console.log('🎯 ChatInterface rendered. Messages count:', messages.length);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  useEffect(() => {
    const t = requestAnimationFrame(() => {
      scrollToBottom();
    });
    return () => cancelAnimationFrame(t);
  }, [messages, loading]);

  useEffect(() => {
    if (!user?.id || !getToken()) {
      setRagContext(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.CHAT_RAG_CONTEXT);
        if (!cancelled) {
          setRagContext(res.data);
        }
      } catch (err) {
        console.warn('Chat RAG context banner skipped:', err?.message || err);
        if (!cancelled) {
          setRagContext(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Load chat session from sessionStorage on mount
  useEffect(() => {
    const loadSessionFromStorage = () => {
      try {
        const savedUserId = sessionStorage.getItem(STORAGE_KEYS.USER_ID);
        const currentUserId = user?.id;

        // Check if user has changed
        if (savedUserId && currentUserId && savedUserId !== currentUserId) {
          console.log('🔄 User changed! Clearing previous user chat session');
          console.log('   Previous user:', savedUserId);
          console.log('   Current user:', currentUserId);
          
          // Clear all chat data for previous user
          sessionStorage.removeItem(STORAGE_KEYS.MESSAGES);
          sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
          sessionStorage.removeItem(STORAGE_KEYS.CONTEXT);
          sessionStorage.removeItem(STORAGE_KEYS.USER_ID);
          
          setMessages([]);
          setSessionId(null);
          setConversationContext(null);
          setIsInitialized(true);
          return;
        }

        // Load saved session for current user
        const savedMessages = sessionStorage.getItem(STORAGE_KEYS.MESSAGES);
        const savedSessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
        const savedContext = sessionStorage.getItem(STORAGE_KEYS.CONTEXT);

        if (savedMessages) {
          const parsedMessages = JSON.parse(savedMessages);
          console.log('📦 Restored chat session from storage:', parsedMessages.length, 'messages');
          setMessages(parsedMessages);
        }

        if (savedSessionId) {
          console.log('📦 Restored session ID:', savedSessionId);
          setSessionId(savedSessionId);
        }

        if (savedContext) {
          console.log('📦 Restored conversation context from storage');
          setConversationContext(savedContext);
        }

        // Store current user ID
        if (currentUserId) {
          sessionStorage.setItem(STORAGE_KEYS.USER_ID, currentUserId);
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('❌ Failed to load chat session from storage:', error);
        setIsInitialized(true);
      }
    };

    loadSessionFromStorage();
  }, [user]);

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    if (isInitialized && messages.length > 0) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages));
        console.log('💾 Saved messages to sessionStorage:', messages.length);
      } catch (error) {
        console.error('❌ Failed to save messages to storage:', error);
      }
    }
  }, [messages, isInitialized]);

  // Save sessionId to sessionStorage whenever it changes
  useEffect(() => {
    if (isInitialized && sessionId) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
        console.log('💾 Saved session ID to sessionStorage:', sessionId);
      } catch (error) {
        console.error('❌ Failed to save session ID to storage:', error);
      }
    }
  }, [sessionId, isInitialized]);

  // Save conversation context to sessionStorage whenever it changes
  useEffect(() => {
    if (isInitialized && conversationContext) {
      try {
        sessionStorage.setItem(STORAGE_KEYS.CONTEXT, conversationContext);
        console.log('💾 Saved conversation context to sessionStorage');
      } catch (error) {
        console.error('❌ Failed to save context to storage:', error);
      }
    }
  }, [conversationContext, isInitialized]);

  // Auto-load greeting only if no saved messages
  useEffect(() => {
    const loadGreeting = async () => {
      if (isInitialized && messages.length === 0) {  // Only if no saved messages
        try {
          const token = getToken();
          if (!token) return;

          console.log('🎉 Loading personalized greeting with context...');
          const response = await apiClient.post(API_ENDPOINTS.CHAT_GREETING, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.data && response.data.greeting) {
            const greetingMessage = {
              id: chatMessageId('g'),
              role: 'assistant',
              content: response.data.greeting,
              timestamp: new Date().toISOString(),
              isGreeting: true  // Flag for special styling
            };
            
            console.log('✅ Greeting loaded:', response.data.display_name);
            setMessages([greetingMessage]);
            
            // Store conversation context if provided
            if (response.data.conversation_context) {
              console.log('📝 Conversation context loaded');
              setConversationContext(response.data.conversation_context);
            }
          }
        } catch (error) {
          console.error('Failed to load greeting:', error);
          // Fail silently - user can still chat normally
        }
      }
    };
    
    loadGreeting();
  }, [isInitialized]);  // Run after initialization

  const handleSendMessage = async () => {
    if (inputMessage.trim() === '') return;

    const userMessage = {
      id: chatMessageId('u'),
      content: inputMessage,
      role: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // Get token from auth context
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log('🔵 Sending chat message:', inputMessage);
      console.log('📝 Conversation context:', conversationContext ? 'Available' : 'None');
      
      const response = await apiClient.post(API_ENDPOINTS.CHAT, {
        message: inputMessage,
        session_id: sessionId,
        use_rag: true,
        conversation_context: conversationContext  // Send context for AI to use
      }, {
        timeout: CHAT_REQUEST_TIMEOUT_MS,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🟢 Chat API Response:', response.data);

      if (response.data.success) {
        const aiMessage = {
          id: chatMessageId('a'),
          content: response.data.response,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          confidence_score: response.data.confidence_score,
          processing_time: response.data.processing_time,
        };

        console.log('✅ Adding AI message to state:', aiMessage);
        setMessages(prev => {
          const newMessages = [...prev, aiMessage];
          console.log('📝 New messages state:', newMessages);
          return newMessages;
        });
        
        if (!sessionId) {
          setSessionId(response.data.session_id);
          console.log('🆔 Session ID set:', response.data.session_id);
        }
      } else {
        console.error('❌ Response success is false');
        throw new Error('Failed to get response');
      }
    } catch (error) {
      const fallback = 'Sorry, I encountered an error while processing your request. Please try again.';
      console.error('🔴 Chat error:', error);
      console.error('🔴 API detail:', error.response?.data?.detail, 'body:', error.response?.data);
      const detail = getApiErrorMessage(error, fallback);
      const content = detail.length > 320 ? `${detail.slice(0, 317)}…` : detail;
      const errorMessage = {
        id: chatMessageId('e'),
        content,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        error: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    console.log('🔄 Starting new chat session...');
    
    // Clear messages, session, and context
    setMessages([]);
    setSessionId(null);
    setInputMessage('');
    setConversationContext(null);
    
    // Clear sessionStorage
    try {
      sessionStorage.removeItem(STORAGE_KEYS.MESSAGES);
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      sessionStorage.removeItem(STORAGE_KEYS.CONTEXT);
      console.log('✅ Cleared sessionStorage and context');
    } catch (error) {
      console.error('❌ Failed to clear sessionStorage:', error);
    }
    
    // Reload greeting with context
    setTimeout(async () => {
      try {
        const token = getToken();
        if (!token) return;

        console.log('🎉 Loading greeting for new session...');
        const response = await apiClient.post(API_ENDPOINTS.CHAT_GREETING, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.greeting) {
          const greetingMessage = {
            id: chatMessageId('g'),
            role: 'assistant',
            content: response.data.greeting,
            timestamp: new Date().toISOString(),
            isGreeting: true
          };
          
          console.log('✅ New greeting loaded');
          setMessages([greetingMessage]);
          
          // Store new conversation context if provided
          if (response.data.conversation_context) {
            console.log('📝 New conversation context loaded');
            setConversationContext(response.data.conversation_context);
          }
        }
      } catch (error) {
        console.error('Failed to load greeting:', error);
      }
    }, 100);
  };


  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0, px: 2, pt: 2 }}>
        <PageSectionHeader
          icon={<ChatNavigationIcon />}
          title="Chat Interface"
          description={buildChatHeaderDescription(ragContext)}
        />
      </Box>

      {/* Messages Area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary',
              py: 4
            }}
          >
            <ChatBotIcon size={80} />
            <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 500, mt: 2 }}>
              Welcome to Nexus-AI
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              AI-Powered Voice Analysis & Processing
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Start a conversation by typing a message below
            </Typography>
          </Box>
        )}
        
        {messages.map((message) => (
          <Fade key={message.id} in appear timeout={280}>
            <Box sx={{ mb: 2 }}>
          <Stack
            direction={message.role === 'user' ? 'row-reverse' : 'row'}
            spacing={1}
            alignItems="flex-start"
          >
            {message.role === 'user' ? (
              <Avatar sx={{ bgcolor: 'primary.light' }}>
                <PersonIcon />
              </Avatar>
            ) : (
              <Box sx={{ 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <ChatBotIcon size={40} />
              </Box>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                maxWidth: '70%',
                bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                boxShadow: message.role === 'user' ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                wordBreak: 'break-word',
              }}
            >
              <Typography variant="caption" display="block" gutterBottom>
                {message.role === 'user' ? 'You' : 'Nexus-AI'} -{' '}
                {new Date(message.timestamp).toLocaleTimeString()}
              </Typography>
              {/* Greeting: plain text (no Markdown) so underscores / slugs are not styled oddly */}
              {message.role === 'assistant' ? (
                message.isGreeting ? (
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}
                  >
                    {message.content}
                  </Typography>
                ) : (
                <ReactMarkdown
                  components={{
                    // Style paragraphs
                    p: ({node, ...props}) => (
                      <Typography 
                        variant="body1" 
                        paragraph 
                        sx={{ mb: 1, '&:last-child': { mb: 0 } }}
                        {...props} 
                      />
                    ),
                    // Style list items
                    li: ({node, ...props}) => (
                      <li style={{ marginBottom: '4px', marginLeft: '16px' }} {...props} />
                    ),
                    // Style bold text
                    strong: ({node, ...props}) => (
                      <strong style={{ color: '#13213c', fontWeight: 600 }} {...props} />
                    ),
                    // Style code
                    code: ({node, inline, ...props}) => (
                      inline ? 
                        <code style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          fontFamily: 'monospace'
                        }} {...props} /> :
                        <code style={{
                          display: 'block',
                          backgroundColor: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '4px',
                          overflowX: 'auto',
                          fontFamily: 'monospace'
                        }} {...props} />
                    ),
                    // Style headings
                    h3: ({node, ...props}) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 1, fontWeight: 600 }} {...props} />
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
                )
              ) : (
                <Typography variant="body1">{message.content}</Typography>
              )}
              
              {message.error && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <ErrorIcon color="error" fontSize="small" />
                  <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
                    Error
                  </Typography>
                </Box>
              )}
              
              {message.processing_time && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Processed in {message.processing_time.toFixed(2)}s
                </Typography>
              )}
            </Paper>
          </Stack>
            </Box>
          </Fade>
        ))}
        
        <Fade in={loading} timeout={220} mountOnEnter unmountOnExit>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <ChatBotIcon size={40} />
            </Box>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                maxWidth: '70%',
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" color="text.secondary">
                  Nexus-AI is thinking...
                </Typography>
              </Box>
            </Paper>
          </Stack>
          </Box>
        </Fade>
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message here..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !loading) {
              handleSendMessage();
            }
          }}
          sx={{
            transition: 'opacity 0.2s ease',
            opacity: loading ? 0.88 : 1,
          }}
        />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={loading || inputMessage.trim() === ''}
          sx={{ 
            height: '56px',
            minWidth: '100px'
          }}
        >
          Send
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleNewChat}
          disabled={loading}
          sx={{ 
            height: '56px',
            minWidth: '120px',
            whiteSpace: 'nowrap'
          }}
        >
          New Chat
        </Button>
      </Paper>
    </Box>
  );
};

export default ChatInterface;